import { useEffect } from 'react';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { App } from '@capacitor/app';
import { isNativePlatform, isIOS } from '@/lib/platform';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { useKeyboardAvoidance } from './useKeyboardAvoidance';

export const useMobileSetup = () => {
  // Enable keyboard avoidance for all inputs
  useKeyboardAvoidance();
  useEffect(() => {
    if (!isNativePlatform()) return;

    const setupMobile = async () => {
      // Configure StatusBar
      try {
        await StatusBar.setStyle({ style: Style.Dark });
        if (isIOS()) {
          await StatusBar.setOverlaysWebView({ overlay: true });
        }
      } catch (error) {
        logger.warn('useMobileSetup', 'StatusBar setup failed');
      }

      // Configure Keyboard
      try {
        Keyboard.setAccessoryBarVisible({ isVisible: true });
      } catch (error) {
        logger.warn('useMobileSetup', 'Keyboard setup failed');
      }

      // Handle app state changes
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          // Refresh session when app becomes active
          supabase.auth.getSession();
        }
      });

      // ✅ Deep link handling moved to App.tsx (global scope, registered at boot)
      // This prevents race conditions where link arrives before hook registers
      
      // Handle Supabase auth callback (still needed here for Supabase auth flow)
      App.addListener('appUrlOpen', (data) => {
        const url = data.url;
        logger.info('useMobileSetup', 'Deep link received', { url });
        
        // Handle Supabase auth callback
        if (url.includes('auth/callback')) {
          const params = new URL(url).searchParams;
          const access_token = params.get('access_token');
          const refresh_token = params.get('refresh_token');
          
          if (access_token && refresh_token) {
            supabase.auth.setSession({
              access_token,
              refresh_token
            });
            
            // Navigate to app
            window.location.href = '/app';
          }
        }
      });
    };

    setupMobile();

    return () => {
      if (isNativePlatform()) {
        App.removeAllListeners();
      }
    };
  }, []);
};
